import { useEffect, useRef } from 'react'

import { JourneyOutcome } from '@cash-web/protos/squareup/cash/blockuserjourneys/api/v1/service.pb'
import { UserJourneysPlugin } from '@cash-web/shared-echo-util-main'
import type { UserJourneyAction, UserJourneyState } from '@cash-web/shared-user-journey-data-access-context'
import { useUserJourneyContext } from '@cash-web/shared-user-journey-data-access-context'

import { useEchoPluginsSafe } from './EchoPluginProvider'

const OUTCOME_MAP: Record<JourneyOutcome, string> = {
  [JourneyOutcome.JOURNEY_OUTCOME_UNSPECIFIED]: 'UNSPECIFIED',
  [JourneyOutcome.JOURNEY_OUTCOME_SUCCEEDED]: 'SUCCESS',
  [JourneyOutcome.JOURNEY_OUTCOME_FAILED]: 'FAILURE',
  [JourneyOutcome.JOURNEY_OUTCOME_USER_CANCELED]: 'CANCEL',
  [JourneyOutcome.JOURNEY_OUTCOME_PROCESS_CRASHED]: 'PROCESS_CRASHED',
  [JourneyOutcome.JOURNEY_OUTCOME_PROCESS_KILLED]: 'PROCESS_KILLED',
  [JourneyOutcome.JOURNEY_OUTCOME_TERMINATED]: 'TERMINATED',
}

/**
 * Bridge component that connects UserJourneyContext to Echo desktop.
 * Sends all journey events (start, complete, tags, variants, signals) to Echo.
 */
export const EchoUserJourneyBridge = () => {
  const userJourneyContext = useUserJourneyContext()
  const pluginRef = useRef<UserJourneysPlugin | null>(null)
  const echoContext = useEchoPluginsSafe()
  const echoClientRef = useRef(echoContext?.client)

  useEffect(() => {
    if (!echoContext) return

    const { client, registerPlugin, unregisterPlugin } = echoContext
    if (!client) return

    echoClientRef.current = client
    const plugin = new UserJourneysPlugin(client)
    pluginRef.current = plugin
    registerPlugin(plugin)

    return () => {
      if (pluginRef.current) {
        unregisterPlugin(pluginRef.current.id)
      }
    }
  }, [echoContext])

  useEffect(() => {
    if (!pluginRef.current) {
      return
    }

    const plugin = pluginRef.current

    const unsubscribe = userJourneyContext.subscribe(
      ({
        prevState,
        nextState,
        action,
      }: {
        prevState: UserJourneyState
        nextState: UserJourneyState
        action: UserJourneyAction
      }) => {
        const journey =
          action.type === 'USER_JOURNEY_COMPLETE' ? prevState.journeys[action.name] : nextState.journeys[action.name]

        if (!journey) {
          echoClientRef.current?.logger.warn(
            '[EchoUserJourneyBridge] No journey found for action:',
            action.type,
            action.name
          )
          return
        }

        switch (action.type) {
          case 'USER_JOURNEY_START':
            plugin.didStartJourney(journey.id, journey.created, journey.name, 300, {})
            break

          case 'USER_JOURNEY_COMPLETE':
            plugin.didEndJourney(journey.id, journey.name, OUTCOME_MAP[action.outcome] || 'UNSPECIFIED')
            break

          case 'USER_JOURNEY_ADD_FRUSTRATION':
            plugin.didAddFrustrationSignal(journey.id, journey.name, action.frustration)
            break

          case 'USER_JOURNEY_ADD_FRICTION':
            plugin.didAddFrictionSignal(journey.id, journey.name, action.friction)
            break

          case 'USER_JOURNEY_ADD_TAG':
            plugin.didAddTag(journey.id, journey.name, action.tag)
            break

          case 'USER_JOURNEY_SET_VARIANT':
            plugin.didAddVariant(journey.id, journey.name, action.variant)
            break
        }

        const activeJourneys = nextState.history.map(id => nextState.journeys[id]).filter(Boolean)
        const currentJourneyName = nextState.active ? nextState.journeys[nextState.active]?.name || null : null

        plugin.didUpdateActiveJourneys(JSON.stringify(activeJourneys), currentJourneyName)
      }
    )

    return unsubscribe
  }, [userJourneyContext.subscribe, pluginRef])

  return null
}
